import { inject, Injectable, Injector } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, mergeMap, of, tap } from "rxjs";

import { AuthenticationService } from "../../_services/authentication.service";
import { NotificationService } from "../../_services/notification.service";
import { ProductActions } from "./index";
import { ProductService } from "./product.service";

// AuthenticationService is resolved lazily through the injector rather than
// eagerly in a field initializer. See AuthenticationEffects for the full
// reasoning — eager injection here defeats the AuthenticationService factory's
// branch on AuthModeService.mode.
@Injectable()
export class ProductEffects {
  private actions$ = inject(Actions);
  private injector = inject(Injector);
  private notificationService = inject(NotificationService);
  private productService = inject(ProductService);

  private readonly PRODUCT_KEY = "product";

  switchProduct$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProductActions.switchProduct),
        tap(action => {
          this.injector.get(AuthenticationService).switchProduct(action.id!);
        })
      ),
    { dispatch: false }
  );

  loadAvailableProductsDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.loadAvailableProductsDetails),
      mergeMap(() =>
        this.productService.getAvailableProductsDetails().pipe(
          map(response =>
            ProductActions.getAvailableProductDetailsSuccess({
              products: response,
            })
          ),
          catchError(error =>
            of(ProductActions.productActionFailure({ error: error.message }))
          )
        )
      )
    )
  );

  editProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.editProduct),
      mergeMap(action =>
        this.productService.editProduct(action.product).pipe(
          map(product => ProductActions.editProductSuccess({ product })),
          catchError(error =>
            of(ProductActions.productActionFailure({ error: error.error }))
          )
        )
      )
    )
  );

  createProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.createProduct),
      mergeMap(action =>
        this.productService.createProduct(action.product).pipe(
          map(product => ProductActions.createProductSuccess({ product })),
          catchError(error =>
            of(
              ProductActions.productActionFailure({
                error: error.error,
              })
            )
          )
        )
      )
    )
  );

  createProductSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProductActions.createProductSuccess),
        tap(() =>
          this.notificationService.showSuccess("Product created successfully!")
        )
      ),
    { dispatch: false }
  );

  editProductSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProductActions.editProductSuccess),
        tap(() =>
          this.notificationService.showSuccess("Product updated successfully!")
        )
      ),
    { dispatch: false }
  );

  productActionFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProductActions.productActionFailure),
        tap(action => {
          this.notificationService.showError(action.error);
        })
      ),
    { dispatch: false }
  );
}
